import { createServerFn } from "@tanstack/react-start";
import { zod$ } from "~/lib/server/fn-utils";
import { assertFeatureEnabled } from "~/tenant/feature-gates";
import { accountLockStatusSchema } from "./security.schemas";

const getSessionUserId = async () => {
  const { getSessionFromHeaders } = await import("~/lib/auth/session");
  const { getRequest } = await import("@tanstack/react-start/server");
  const { headers } = getRequest();
  const session = await getSessionFromHeaders(headers);
  return session?.user?.id ?? null;
};

export const getAccountLockStatus = createServerFn({ method: "GET" })
  .inputValidator(zod$(accountLockStatusSchema))
  .handler(async ({ data }) => {
    await assertFeatureEnabled("security_core");
    const sessionUserId = await getSessionUserId();
    const { unauthorized, forbidden } = await import("~/lib/server/errors");
    if (!sessionUserId) {
      throw unauthorized("User not authenticated");
    }

    if (data.userId !== sessionUserId) {
      const { PermissionService } = await import("~/features/roles/permission.service");
      const isGlobalAdmin = await PermissionService.isGlobalAdmin(sessionUserId);

      if (!isGlobalAdmin) {
        const { getRequest } = await import("@tanstack/react-start/server");
        const organizationId = getRequest().headers.get("x-organization-id");
        if (!organizationId) {
          throw forbidden("Organization context required");
        }

        const { requireOrganizationMembership, ORG_ADMIN_ROLES } = await import(
          "~/lib/auth/guards/org-guard"
        );
        await requireOrganizationMembership(
          { userId: sessionUserId, organizationId },
          { roles: ORG_ADMIN_ROLES },
        );
        await requireOrganizationMembership({ userId: data.userId, organizationId });
      }
    }

    const { isAccountLocked } = await import("~/lib/security/lockout");
    return isAccountLocked(data.userId);
  });
